import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Preferences } from "@capacitor/preferences";
import { clearStore, STORES } from "@/lib/db";

// API 서버 URL (환경변수로 설정)
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:23998";

// 토큰 저장 키
const TOKEN_KEY = "auth_token";

// 메모리 캐시 (동기 접근용)
let cachedToken: string | null = null;

/**
 * JWT 토큰의 만료 여부를 확인
 * base64로 인코딩된 payload에서 exp 클레임을 파싱하여 현재 시간과 비교
 * @returns true이면 만료됨
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    // base64url → base64 변환 후 디코딩
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(payload));

    // exp 클레임이 없거나 숫자가 아닌 토큰은 만료된 것으로 간주 (보안)
    if (typeof decoded.exp !== "number") return true;

    // exp는 초 단위, Date.now()는 밀리초 단위
    return decoded.exp * 1000 < Date.now();
  } catch {
    // 파싱 실패 시 만료된 것으로 간주
    return true;
  }
}

/**
 * 토큰 초기화 (앱 시작 시 호출)
 * Capacitor Preferences에서 토큰을 로드하여 메모리 캐시에 저장
 * 만료된 토큰은 사전 제거하여 불필요한 API 요청 방지
 */
export async function initAuthToken(): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value && isTokenExpired(value)) {
      // 만료된 토큰은 즉시 삭제
      cachedToken = null;
      await Preferences.remove({ key: TOKEN_KEY });
      return;
    }
    cachedToken = value;
  } catch {
    // 브라우저 환경 폴백
    const value = localStorage.getItem(TOKEN_KEY);
    if (value && isTokenExpired(value)) {
      cachedToken = null;
      localStorage.removeItem(TOKEN_KEY);
      return;
    }
    cachedToken = value;
  }
}

/**
 * 토큰 저장
 * 메모리 캐시와 Capacitor Preferences 모두 업데이트
 */
export function setAuthToken(token: string): void {
  cachedToken = token;
  // 비동기로 Preferences에 저장 (실패해도 메모리 캐시는 유지)
  Preferences.set({ key: TOKEN_KEY, value: token }).catch(() => {
    // 브라우저 환경 폴백
    localStorage.setItem(TOKEN_KEY, token);
  });
}

/**
 * 토큰 조회 (동기)
 * 메모리 캐시에서 즉시 반환
 */
export function getAuthToken(): string | null {
  return cachedToken;
}

/**
 * 토큰 삭제
 * 메모리 캐시와 Capacitor Preferences 모두 삭제
 */
export function clearAuthToken(): void {
  cachedToken = null;
  Preferences.remove({ key: TOKEN_KEY }).catch(() => {
    // 브라우저 환경 폴백
    localStorage.removeItem(TOKEN_KEY);
  });
}

const httpLink = createHttpLink({
  uri: `${API_URL}/graphql`,
  credentials: "include", // 쿠키도 함께 전송 (웹 호환)
});

// Authorization 헤더 추가
const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

// 중복 로그아웃 방지 플래그
let isForceLoggingOut = false;

/**
 * Apollo 캐시 + IndexedDB 전체 정리
 * 회사 전환, 로그아웃 등에서 공용으로 사용 (DRY)
 */
export async function clearAllCaches(): Promise<void> {
  await Promise.all([
    apolloClient.clearStore().catch(() => {}),
    ...Object.values(STORES).map((store) => clearStore(store).catch(() => {})),
  ]);
}

/**
 * 강제 로그아웃 처리 (전역)
 * 토큰 삭제 + Apollo 캐시 + IndexedDB 전체 정리 후 로그인 페이지로 이동
 * errorLink, upload 401, useAuth 등에서 공용으로 사용
 */
export async function forceLogout(): Promise<void> {
  // 중복 호출 방지 (여러 쿼리가 동시에 UNAUTHENTICATED 반환하는 경우)
  if (isForceLoggingOut) return;
  isForceLoggingOut = true;

  // 1. 토큰 즉시 삭제 (동기, 추가 API 요청 방지)
  clearAuthToken();

  // 2. Apollo 캐시 + IndexedDB 정리 (비동기, 완료 후 redirect)
  try {
    await clearAllCaches();
  } catch {
    // 정리 실패해도 로그인 페이지로 이동
  }

  // 3. 정리 완료 후 리다이렉트
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }

  isForceLoggingOut = false;
}

// 에러 처리 link: UNAUTHENTICATED 에러 시 자동 로그아웃 + DEV 로깅
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // UNAUTHENTICATED GraphQL 에러 감지
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") {
        forceLogout();
        return;
      }
    }
  }

  // 네트워크 401 에러 감지
  if (networkError && "statusCode" in networkError && networkError.statusCode === 401) {
    forceLogout();
    return;
  }

  // DEV 환경에서 기타 에러 로깅
  if (import.meta.env.DEV) {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL Error] Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Operation: ${operation.operationName}`
        );
      });
    }
    if (networkError) {
      console.error(`[Network Error] ${networkError.message}`);
    }
  }
});

// __typename 자동 제거 (mutation 시 필요)
const removeTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key: string, value: unknown) =>
      key === "__typename" ? undefined : value;
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }
  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, removeTypenameLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});
